// src/components/SystemRecommendation/components/FitsYourHouse.tsx

import React from 'react';
import { HouseFitData, ViewMode } from '../types';
import CollapsibleSection from './CollapsibleSection';

interface FitsYourHouseProps {
  data: HouseFitData;
  viewMode: ViewMode;
}

const FitsYourHouse: React.FC<FitsYourHouseProps> = ({ data, viewMode }) => {
  return (
    <CollapsibleSection
      title="This is why it fits your house"
      icon="🏠"
      confidenceLevel={data.confidence.level}
      summary={data.confidence.summary}
    >
      {viewMode === 'customer' ? (
        <div className="customer-content">
          <div className="fit-section">
            <h4>Your Property</h4>
            <p>
              <strong>{data.physicalCompatibility.propertyType}</strong> • {data.physicalCompatibility.propertySize}
            </p>
            {data.physicalCompatibility.existingInfrastructure.length > 0 && (
              <>
                <p className="subsection-label">What you already have:</p>
                <ul>
                  {data.physicalCompatibility.existingInfrastructure.map((item, i) => (
                    <li key={i}>✓ {item}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="fit-section">
            <h4>Water & Gas Supply</h4>
            <p>
              <strong>Water pressure:</strong> {data.flowAndPressure.adequate ? '✓ Adequate' : '⚠️ Needs checking'}
            </p>
            <p className="detail-text">{data.flowAndPressure.details}</p>
            {data.gasSupply.available && (
              <>
                <p><strong>Gas supply:</strong> ✓ Available</p>
                <p className="detail-text">{data.gasSupply.capacity}</p>
              </>
            )}
          </div>

          <div className="fit-section">
            <h4>Where Everything Goes</h4>
            <ul>
              <li><strong>Boiler:</strong> {data.installationLocations.boiler}</li>
              {data.installationLocations.cylinder && (
                <li><strong>Hot water cylinder:</strong> {data.installationLocations.cylinder}</li>
              )}
              <li><strong>Controls:</strong> {data.installationLocations.controls}</li>
            </ul>
          </div>

          <div className="fit-section">
            <h4>Installation & Disruption</h4>
            <p><strong>Installation time:</strong> {data.disruption.days} days</p>
            <p className="detail-text">{data.disruption.impact}</p>
            {data.buildingWork.required ? (
              <>
                <p className="warning-text">⚠️ Some building work required:</p>
                <ul>
                  {data.buildingWork.details.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="success-text">✓ No major building work needed</p>
            )}
          </div>
        </div>
      ) : (
        <div className="technical-content">
          <div className="tech-grid">
            <div className="tech-item">
              <span className="tech-label">Property Type:</span>
              <span className="tech-value">{data.physicalCompatibility.propertyType}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Size:</span>
              <span className="tech-value">{data.physicalCompatibility.propertySize}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Water Pressure:</span>
              <span className="tech-value">
                {data.flowAndPressure.adequate ? 'Adequate' : 'Verify required'}
              </span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Gas Supply:</span>
              <span className="tech-value">
                {data.gasSupply.available ? `Available (${data.gasSupply.capacity})` : 'Not available'}
              </span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Boiler Location:</span>
              <span className="tech-value">{data.installationLocations.boiler}</span>
            </div>
            {data.installationLocations.cylinder && (
              <div className="tech-item">
                <span className="tech-label">Cylinder Location:</span>
                <span className="tech-value">{data.installationLocations.cylinder}</span>
              </div>
            )}
            <div className="tech-item">
              <span className="tech-label">Installation Duration:</span>
              <span className="tech-value">{data.disruption.days} days</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Building Work:</span>
              <span className="tech-value">{data.buildingWork.required ? 'Required' : 'Not required'}</span>
            </div>
          </div>
          {data.buildingWork.required && data.buildingWork.details.length > 0 && (
            <div className="tech-notes">
              <p><strong>Building work required:</strong></p>
              <ul>
                {data.buildingWork.details.map((detail, i) => (
                  <li key={i}>{detail}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
};

export default FitsYourHouse;
